import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiCheckCircle, FiXCircle, FiCreditCard, FiChevronLeft, FiChevronRight, FiX, FiEdit2, FiExternalLink, FiRefreshCw, FiRepeat, FiCalendar, FiList } from 'react-icons/fi';
import { ExpensesAPI, VendorsAPI, StatementsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import PaymentModal from '../components/PaymentModal';
import BulkUploadModal from '../components/BulkUploadModal';
import StatementUploadModal from '../components/StatementUploadModal';
import MatchReviewPanel from '../components/MatchReviewPanel';
import { colors, matte, tableStyles, billingTableCSS } from '../styles/billingTableStyles';

const STATUS_COLORS = {
  DRAFT: { bg: colors.stone100, fg: colors.stone600 },
  PENDING: { bg: colors.amber50, fg: colors.amber600 },
  APPROVED: { bg: colors.blue50, fg: colors.blue600 },
  REJECTED: { bg: colors.rose50, fg: colors.rose600 },
  PAID: { bg: colors.emerald50, fg: colors.emerald600 },
};

const CATEGORY_LABELS = {
  TRAVEL: 'Travel', OFFICE_SUPPLIES: 'Office Supplies', EQUIPMENT: 'Equipment',
  SERVICES: 'Services', UTILITIES: 'Utilities', FOOD: 'Food',
  TRANSPORT: 'Transport', PAYROLL: 'Payroll', RENT_UTILITIES: 'Rent & Utilities',
  MARKETING: 'Marketing', SOFTWARE_SUBSCRIPTIONS: 'Software & Subscriptions',
  FOOD_ENTERTAINMENT: 'Food & Entertainment', LOGISTICS: 'Logistics',
  PROFESSIONAL_FEES: 'Professional Fees', OTHER: 'Other',
};

const CATEGORY_COLORS = {
  TRAVEL: matte.blue, OFFICE_SUPPLIES: matte.purple, EQUIPMENT: matte.teal,
  SERVICES: matte.amber, UTILITIES: matte.grey, FOOD: matte.green,
  TRANSPORT: matte.red, PAYROLL: matte.blue, RENT_UTILITIES: matte.red,
  MARKETING: matte.purple, SOFTWARE_SUBSCRIPTIONS: matte.teal,
  FOOD_ENTERTAINMENT: matte.green, LOGISTICS: matte.amber,
  PROFESSIONAL_FEES: matte.grey, OTHER: matte.grey,
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtCurr = (n, curr = 'INR') => {
  const currency = String(curr || 'INR').toUpperCase();
  try {
    return Number(n || 0).toLocaleString('en-IN', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } catch (_) {
    return `${currency} ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const toMoneyNumber = (v) => {
  if (v === null || v === undefined || v === '') return NaN;
  const s = String(v).replace(/,/g, '').trim();
  if (!s) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const activeRows = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((row) => String(row?.status || '').toLowerCase() !== 'voided');

const byLatest = (a, b) => (
  Number(b?.sequenceNo || 0) - Number(a?.sequenceNo || 0)
  || Number(b?.id || 0) - Number(a?.id || 0)
);

const latestVendorPayment = (expense) => activeRows(expense?.vendorPayments).sort(byLatest)[0] || null;
const latestReimbursement = (expense) => activeRows(expense?.reimbursements).sort(byLatest)[0] || null;

const paymentActor = (expense) => {
  const latest = expense?.isReimbursement ? latestReimbursement(expense) : latestVendorPayment(expense);
  return latest?.recordedByUser || null;
};

const paymentPaidName = (expense) => {
  const latest = expense?.isReimbursement ? latestReimbursement(expense) : latestVendorPayment(expense);
  if (expense?.isReimbursement) {
    const accountHolderName = expense?.statementTransaction?.statement?.accountHolderName || '';
    return accountHolderName || latest?.paidToNameSnapshot || '';
  }
  return latest?.payeeName || '';
};

const styles = {
  page: { minHeight: '100vh', background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`, padding: '24px 32px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, color: colors.stone800, margin: 0 },
  addBtn: { textTransform: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 },
  statsRow: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  statCard: { flex: '1 1 160px', background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' },
  statCardClickable: (active) => ({
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'transform .15s ease, box-shadow .15s ease',
    boxShadow: active ? '0 0 0 3px rgba(17, 24, 39, 0.10), 0 1px 3px rgba(0,0,0,.06)' : '0 1px 3px rgba(0,0,0,.06)',
  }),
  statLabel: { fontSize: 12, fontWeight: 500, color: colors.stone500, marginBottom: 4 },
  statValue: { fontSize: 22, fontWeight: 700, color: colors.stone800 },
  // Category segmented bar (Invoices-like)
  segBar: { display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: colors.stone200, marginTop: 10 },
  // Category legend as a single line (wraps)
  legendGrid: { display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10, alignItems: 'center' },
  legendItem: (active) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    background: '#fff',
    border: `1px solid ${active ? colors.stone800 : colors.stone200}`,
    borderRadius: 10,
    fontSize: 12,
    color: colors.stone800,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    boxShadow: active ? '0 0 0 3px rgba(17, 24, 39, 0.08)' : 'none'
  }),
  searchBox: { display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${colors.stone200}`, borderRadius: 10, padding: '6px 12px', minWidth: 260 },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, background: 'transparent' },
  monthYearSelect: {
    border: `1px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    fontSize: 12,
    padding: '6px 8px',
    color: colors.stone700
  },
  ...tableStyles,
  table: { ...tableStyles.table, minWidth: 1100 },
  badge: (_bg, fg) => ({ ...tableStyles.badge, color: fg }),
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 },
  pageBtn: (disabled) => ({ padding: '6px 12px', borderRadius: 6, border: `1px solid ${colors.stone200}`, background: disabled ? colors.stone100 : '#fff', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .5 : 1 }),
  empty: { textAlign: 'center', padding: 60, color: colors.stone400, fontSize: 15 },
  actionBtn: { padding: '4px 10px', borderRadius: 6, border: `1px solid ${colors.stone200}`, background: '#fff', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 },

  /* side drawer */
  drawer: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawerContent: {
    width: '70%',
    maxWidth: 1000,
    height: '100%',
    background: '#fff',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  drawerHeader: { padding: '14px 16px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  drawerTitle: { margin: 0, fontSize: 16, fontWeight: 700, color: colors.stone800 },
  drawerBody: { padding: 16, overflow: 'auto' },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: colors.stone600, textTransform: 'uppercase', letterSpacing: '.04em', margin: '14px 0 8px' },
  kv: { display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, fontSize: 13, marginBottom: 8 },
  k: { color: colors.stone500 },
  v: { color: colors.stone800, fontWeight: 600, overflowWrap: 'anywhere' },
  // Plain text block (no "read-only" styling)
  noteBox: { width: '100%', fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', color: colors.stone700 },
  btnRow: { display: 'flex', gap: 8, alignItems: 'center' },
  smallBtn: { fontSize: 12, padding: '4px 10px', borderRadius: 8, height: 25, display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'none' },
  // Plain text "Read-only" (keep color/weight, no pill)
  lockPill: { color: '#991b1b', fontSize: 12, fontWeight: 500 },
  previewWrap: { border: `1px solid ${colors.stone200}`, borderRadius: 12, overflow: 'hidden', background: colors.stone50 },
  previewImg: { width: '100%', maxHeight: 420, objectFit: 'contain', display: 'block', background: colors.stone50 },
  previewPdf: { width: '100%', height: 520, border: 'none', display: 'block' },

  // Drawer layouts
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, alignItems: 'start' },
  twoColTight: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'start' },
  cardLike: { border: `1px solid ${colors.stone200}`, borderRadius: 12, padding: 14, background: '#fff' },
  viewToggleBtn: (active) => ({
    ...tableStyles.btnSecondary,
    borderColor: active ? colors.stone800 : colors.stone200,
    background: active ? colors.stone100 : '#fff',
    color: colors.stone800,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6
  }),
  calendarWrap: { display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 14, alignItems: 'start' },
  calendarCard: { background: '#fff', border: `1px solid ${colors.stone200}`, borderRadius: 12, padding: 12 },
  calendarGrid: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 8 },
  dayCell: (inMonth, isSelected, isToday) => ({
    minHeight: 110,
    border: `1px solid ${isSelected ? colors.stone800 : colors.stone200}`,
    borderRadius: 10,
    padding: 8,
    background: isSelected ? colors.stone50 : '#fff',
    opacity: inMonth ? 1 : 0.55,
    cursor: 'pointer',
    boxShadow: isToday ? '0 0 0 2px rgba(17,24,39,.08)' : 'none'
  }),
  dayDot: (kind) => ({
    width: 8,
    height: 8,
    borderRadius: 999,
    background: kind === 'PROJECTED'
      ? colors.violet600
      : (kind === 'PAID' ? colors.emerald600 : colors.amber600),
    display: 'inline-block'
  })
};

export default function Expenses() {
  const navigate = useNavigate();
  const { company, user } = useAuth();
  const companyId = company?.id;
  const isAdmin = (() => {
    const role = String(user?.role || '').toLowerCase();
    if (role === 'admin') return true;
    const v = user?.isAdmin;
    if (v === true || v === 1) return true;
    if (typeof v === 'string' && ['true', '1', 'yes'].includes(v.trim().toLowerCase())) return true;
    return false;
  })();
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [vendor, setVendor] = useState('');
  const [reimbToFilter, setReimbToFilter] = useState(null);
  const [dateRange, setDateRange] = useState('all'); // 'all' OR { startDate, endDate }
  const [vendorOptions, setVendorOptions] = useState([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [statementModalOpen, setStatementModalOpen] = useState(false);
  const [statements, setStatements] = useState([]);
  const [selectedStatementIds, setSelectedStatementIds] = useState([]);
  const [selectedStatementsById, setSelectedStatementsById] = useState({});
  const [matchFilter, setMatchFilter] = useState('ALL');
  const statementFetchInFlightRef = useRef(new Map());
  const latestSelectedStatementsByIdRef = useRef({});
  const [viewMode, setViewMode] = useState('list'); // list | calendar
  const [calendarCursor, setCalendarCursor] = useState(new Date());
  const [calendarData, setCalendarData] = useState([]);
  const [calendarSelectedYmd, setCalendarSelectedYmd] = useState('');
  const pageSize = 20;
  const vendorDatalistId = useId();

  // Review drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [paidModalOpen, setPaidModalOpen] = useState(false);
  const [paidModalExp, setPaidModalExp] = useState(null);
  const [paidAmount, setPaidAmount] = useState('');
  const [paidReason, setPaidReason] = useState('');
  const [paidSaving, setPaidSaving] = useState(false);

  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [vendorModalLoading, setVendorModalLoading] = useState(false);
  const [vendorModalVendor, setVendorModalVendor] = useState(null);
  const [vendorBillsLoading, setVendorBillsLoading] = useState(false);
  const [vendorBills, setVendorBills] = useState([]);
  const [vendorBillsTotal, setVendorBillsTotal] = useState(0);

  const [exporting, setExporting] = useState(false);

  const toYMD = (d) => {
    if (!d) return '';
    try { return new Date(d).toISOString().slice(0, 10); } catch { return ''; }
  };

  const bankShortcode = (name) => {
    const raw = String(name || '').trim();
    if (!raw) return '-';
    const upper = raw.toUpperCase();
    const token = upper.replace(/[^A-Z0-9]+/g, ' ').trim();
    if (!token) return '-';
    // Prefer known bank acronyms or full-name phrases.
    const knownMatchers = [
      { code: 'SBI', phrases: ['SBI', 'STATE BANK OF INDIA'] },
      { code: 'HDFC', phrases: ['HDFC', 'HDFC BANK'] },
      { code: 'ICICI', phrases: ['ICICI', 'ICICI BANK'] },
      { code: 'AXIS', phrases: ['AXIS', 'AXIS BANK'] },
      { code: 'KOTAK', phrases: ['KOTAK', 'KOTAK MAHINDRA'] },
      { code: 'YES', phrases: ['YES', 'YES BANK'] },
      { code: 'IDFC', phrases: ['IDFC', 'IDFC FIRST'] },
      { code: 'PNB', phrases: ['PNB', 'PUNJAB NATIONAL BANK'] },
      { code: 'BOB', phrases: ['BOB', 'BANK OF BARODA'] },
      { code: 'BOI', phrases: ['BANK OF INDIA'] },
      { code: 'UBI', phrases: ['UNION BANK', 'UNION BANK OF INDIA'] },
      { code: 'CBI', phrases: ['CENTRAL BANK OF INDIA'] },
      { code: 'CANARA', phrases: ['CANARA', 'CANARA BANK'] },
      { code: 'IDBI', phrases: ['IDBI', 'IDBI BANK'] },
      { code: 'FED', phrases: ['FEDERAL BANK'] },
      { code: 'RBL', phrases: ['RBL', 'RBL BANK'] },
      { code: 'INDUSIND', phrases: ['INDUSIND', 'INDUSIND BANK'] },
      { code: 'HSBC', phrases: ['HSBC', 'HONGKONG AND SHANGHAI BANKING'] },
      { code: 'CITI', phrases: ['CITI', 'CITIBANK'] },
      { code: 'SC', phrases: ['STANDARD CHARTERED'] },
      { code: 'DBS', phrases: ['DBS', 'DEVELOPMENT BANK OF SINGAPORE'] },
      { code: 'AMEX', phrases: ['AMEX', 'AMERICAN EXPRESS'] }
    ];
    for (const m of knownMatchers) {
      for (const p of m.phrases) {
        if (p && token.includes(p)) return m.code;
      }
    }
    // Otherwise: acronym of words; fallback to first 4 alphanumerics.
    const parts = token.split(/\s+/).filter(Boolean);
    const acronym = parts.map((p) => p[0]).join('').slice(0, 4);
    if (acronym.length >= 2) return acronym;
    return token.replace(/\s+/g, '').slice(0, 4);
  };

  const statementPeriod = (s) => {
    const start = String(s?.startDate || '').slice(0, 10);
    const end = String(s?.endDate || '').slice(0, 10);
    if (!start && !end) return '';
    if (start && end) return `${start} → ${end}`;
    return start || end;
  };

  const getExpenseFiledForIdentity = (exp) => {
    const type = String(exp?.filedForType || 'USER').toUpperCase() === 'EMPLOYEE' ? 'EMPLOYEE' : 'USER';
    const filedForUser = exp?.user
      ? `${[exp.user.firstName, exp.user.lastName].filter(Boolean).join(' ') || exp.user.email || ''}`.trim()
      : '';
    const filedForEmployee = exp?.filedForEmployee
      ? `${[exp.filedForEmployee.firstName, exp.filedForEmployee.lastName].filter(Boolean).join(' ') || exp.filedForEmployee.email || ''}`.trim()
      : '';
    const label = type === 'EMPLOYEE' ? filedForEmployee : filedForUser;
    return {
      filedForType: type,
      userId: type === 'USER' ? (Number(exp?.userId || 0) || null) : null,
      filedForEmployeeId: type === 'EMPLOYEE' ? (Number(exp?.filedForEmployeeId || 0) || null) : null,
      label
    };
  };

  const buildListParams = (overrides = {}) => {
    const params = { companyId, page, pageSize };
    // Calendar always shows upcoming + paid together (with projections),
    // so do not constrain it by a single status filter.
    if (statusFilter && viewMode !== 'calendar') params.status = statusFilter;
    if (categoryFilter) params.category = categoryFilter;
    if (vendor) params.vendor = vendor;
    if (dateRange && dateRange !== 'all') {
      const fd = toYMD(dateRange.startDate);
      const td = toYMD(dateRange.endDate);
      if (fd) params.fromDate = fd;
      if (td) params.toDate = td;
    }
    return { ...params, ...overrides };
  };

  const monthRange = (d) => {
    const first = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
    return { startDate: first, endDate: last };
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = buildListParams();
      let res;
      if (viewMode === 'calendar') {
        if (!params.fromDate || !params.toDate) {
          const m = monthRange(calendarCursor);
          params.fromDate = toYMD(m.startDate);
          params.toDate = toYMD(m.endDate);
        }
        res = await ExpensesAPI.calendar({ ...params, page: 1, pageSize: 1000 });
      } else {
        res = await ExpensesAPI.list(params);
      }
      setData(res.data || []);
      setStats(res.stats || {});
      setTotal(res.total || 0);
      setCalendarData(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const escapeCsv = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const toSlug = (s) => String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const formatDateTime = (v) => {
    if (!v) return '';
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '';
      // YYYY-MM-DD HH:mm:ss (UTC)
      return d.toISOString().slice(0, 19).replace('T', ' ');
    } catch (_) {
      return '';
    }
  };

  const formatFullAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    if (typeof addr === 'object' && addr?.full) return String(addr.full);
    return '';
  };

  const handleExportCsv = async () => {
    if (!companyId || exporting) return;
    setExporting(true);
    try {
      const exportPageSize = 100;
      const rows = [];
      let p = 1;
      let expectedTotal = null;
      const maxRows = 5000;

      while (true) {
        const res = await ExpensesAPI.list(buildListParams({ page: p, pageSize: exportPageSize }));
        const batch = Array.isArray(res.data) ? res.data : [];
        if (expectedTotal === null) expectedTotal = Number(res.total || 0);

        rows.push(...batch);
        if (rows.length >= maxRows) break;
        if (rows.length >= expectedTotal) break;
        if (batch.length < exportPageSize) break;
        p += 1;
      }

      // Detailed, item-level export (one row per item). Rows are grouped by invoice number.
      const header = [
        'InvoiceNumber',
        'Date',

        'VendorName',
        'VendorGstin',
        'VendorPhone',
        'VendorEmail',
        'VendorAddressFull',
        'BuyerName',
        'BuyerGstin',
        'BuyerPhone',
        'BuyerEmail',
        'BuyerAddressFull',

        'Category',
        'Description',
        'Notes',
        'Currency',
        'ExchangeRate',
        'ConvertedAmountINR',
        'Status',

        'TaxableValue',
        'CgstAmount',
        'SgstAmount',
        'IgstAmount',
        'CessAmount',
        'Total',

        'PaymentMethod',
        'PaymentReference',
        'PaymentDate',
        'AmountPaid',
        'PaymentProofUrl',
        'PaidBy',
        'PaidByName',
        'PayeeName',

        'IsReimbursement',
        'ReimbursementApprover',
        'ReimbursementPaymentMethod',
        'ReimbursementPaymentReference',
        'ReimbursementAmountPaid',
        'ReimbursementProofUrl',
        'ReimbursementShortReason',

        'ReceiptUrl',

        // item fields (breakup)
        'ItemName',
        'ItemDescription',
        'HSNSAC',
        'Quantity',
        'Unit',
        'Price',
        'DiscountAmount',
        'DiscountPercent',
        'ItemTaxableValue',
        'CgstRate',
        'SgstRate',
        'IgstRate',
        'CessRate',
        'ItemCgstAmount',
        'ItemSgstAmount',
        'ItemIgstAmount',
        'ItemCessAmount',
        'LineTotal',
      ];

      const lines = [header.join(',')];
      const sorted = [...rows].sort((a, b) => {
        const ak = String(a?.number || '').trim().toLowerCase();
        const bk = String(b?.number || '').trim().toLowerCase();
        if (ak && bk && ak !== bk) return ak.localeCompare(bk);
        if (ak && !bk) return -1;
        if (!ak && bk) return 1;
        const ad = String(a?.date || '');
        const bd = String(b?.date || '');
        if (ad !== bd) return bd.localeCompare(ad);
        return Number(b?.id || 0) - Number(a?.id || 0);
      });

      for (const exp of sorted) {
        const latestVendor = latestVendorPayment(exp);
        const latestReimb = latestReimbursement(exp);
        const paidByUserName = (() => {
          const actor = paymentActor(exp);
          return actor ? `${[actor.firstName, actor.lastName].filter(Boolean).join(' ') || actor.email || ''}`.trim() : '';
        })();
        const reimbursementApproverName = exp?.reimbursementApprover
          ? `${[exp.reimbursementApprover.firstName, exp.reimbursementApprover.lastName].filter(Boolean).join(' ') || exp.reimbursementApprover.email || ''}`.trim()
          : '';

        const vendorAddressFull = formatFullAddress(exp?.vendorAddress);
        const buyerAddressFull = formatFullAddress(exp?.buyerAddress);

        const items = Array.isArray(exp?.items) ? exp.items : [];
        const itemRows = items.length ? items : [null];

        itemRows.forEach((it) => {
          const row = [
            exp.number || '',
            exp.date || '',

            exp.vendor?.name || exp.vendorName || '',
            exp.vendorGstin || '',
            exp.vendorPhone || '',
            exp.vendorEmail || '',
            vendorAddressFull,
            exp.buyerName || '',
            exp.buyerGstin || '',
            exp.buyerPhone || '',
            exp.buyerEmail || '',
            buyerAddressFull,

            exp.category || '',
            exp.description || '',
            exp.notes || '',
            exp.currency || '',
            exp.exchangeRate ?? '',
            exp.convertedAmount ?? '',
            exp.status || '',

            exp.taxableValue ?? '',
            exp.cgstAmount ?? '',
            exp.sgstAmount ?? '',
            exp.igstAmount ?? '',
            exp.cessAmount ?? '',
            exp.total ?? '',

            latestVendor?.paymentMethod || '',
            latestVendor?.paymentReference || '',
            toYMD(latestVendor?.paidAt),
            exp.vendorPaidTotal ?? '',
            latestVendor?.proofUrl || '',
            paidByUserName,
            paymentPaidName(exp),
            exp.payeeName || '',

            exp.isReimbursement ? 'true' : 'false',
            reimbursementApproverName,
            latestReimb?.paymentMethod || '',
            latestReimb?.paymentReference || '',
            exp.reimbursedTotal ?? '',
            latestReimb?.proofUrl || '',
            latestReimb?.reason || '',

            exp.receiptUrl || '',
            it?.name ?? '',
            it?.description ?? '',
            it?.hsnsac ?? '',
            it?.quantity ?? '',
            it?.unit ?? '',
            it?.price ?? '',
            it?.discountAmount ?? '',
            it?.discountPercent ?? '',
            it?.taxableValue ?? '',
            it?.cgstRate ?? '',
            it?.sgstRate ?? '',
            it?.igstRate ?? '',
            it?.cessRate ?? '',
            it?.cgstAmount ?? '',
            it?.sgstAmount ?? '',
            it?.igstAmount ?? '',
            it?.cessAmount ?? '',
            it?.lineTotal ?? '',
          ].map(escapeCsv);

          lines.push(row.join(','));
        });
      }

      const now = new Date();
      const ymd = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const userLabel = (() => {
        if (isAdmin) return 'all';
        const name = `${[user?.firstName, user?.lastName].filter(Boolean).join(' ')}`.trim();
        if (name) return toSlug(name);
        if (user?.email) return toSlug(String(user.email).split('@')[0]);
        return 'me';
      })();
      const filename = `expenses-${userLabel}-${ymd}.csv`;

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      window.alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { load(); }, [companyId, page, statusFilter, categoryFilter, dateRange, viewMode, calendarCursor]);
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [vendor]);

  // Load vendor options for dropdown autocomplete
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const rows = await VendorsAPI.list({ companyId, limit: 250 });
        setVendorOptions(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [companyId]);

  const loadStatements = async () => {
    if (!companyId) return;
    try {
      const rows = await StatementsAPI.list();
      setStatements(Array.isArray(rows) ? rows : []);
    } catch (e) {
      // ignore silently in expenses screen
    }
  };

  useEffect(() => {
    loadStatements();
  }, [companyId]);

  useEffect(() => {
    latestSelectedStatementsByIdRef.current = selectedStatementsById || {};
  }, [selectedStatementsById]);

  const fetchStatementByIdDedupe = useCallback(async (id) => {
    const statementId = Number(id || 0);
    if (!statementId) return null;
    const inFlight = statementFetchInFlightRef.current.get(statementId);
    if (inFlight) return inFlight;
    const req = StatementsAPI.getById(statementId)
      .finally(() => {
        statementFetchInFlightRef.current.delete(statementId);
      });
    statementFetchInFlightRef.current.set(statementId, req);
    return req;
  }, []);

  useEffect(() => {
    (async () => {
      const ids = Array.isArray(selectedStatementIds) ? selectedStatementIds : [];
      if (ids.length === 0) return;
      const cache = latestSelectedStatementsByIdRef.current || {};
      const missing = ids.filter((id) => !cache?.[id]);
      if (missing.length === 0) return;
      try {
        const rows = await Promise.all(missing.map((id) => fetchStatementByIdDedupe(id)));
        setSelectedStatementsById((prev) => {
          const next = { ...(prev || {}) };
          for (const st of rows) {
            if (st?.id) next[st.id] = st;
          }
          return next;
        });
      } catch (_) {
        // ignore
      }
    })();
  }, [selectedStatementIds, fetchStatementByIdDedupe]);

  const selectedStatements = useMemo(() => {
    const ids = Array.isArray(selectedStatementIds) ? selectedStatementIds : [];
    return ids.map((id) => selectedStatementsById?.[id]).filter(Boolean);
  }, [selectedStatementIds, selectedStatementsById]);

  useEffect(() => {
    const pollIds = selectedStatements
      .map((st) => ({ id: Number(st?.id || 0), status: String(st?.status || '').toUpperCase() }))
      .filter((row) => row.id > 0 && ['QUEUED', 'PARSING', 'MATCHING'].includes(row.status))
      .map((row) => row.id);
    if (!pollIds.length) return undefined;

    let cancelled = false;
    const refresh = async () => {
      try {
        const rows = await Promise.all(pollIds.map((id) => fetchStatementByIdDedupe(id)));
        if (cancelled) return;
        setSelectedStatementsById((prev) => {
          const next = { ...(prev || {}) };
          for (const st of rows) {
            if (st?.id) next[st.id] = st;
          }
          return next;
        });
      } catch (_) {
        // ignore polling failures
      }
    };

    refresh();
    const timer = setInterval(refresh, 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedStatements, fetchStatementByIdDedupe]);

  const combinedStatement = useMemo(() => {
    const txns = selectedStatements.flatMap((s) => (Array.isArray(s?.transactions) ? s.transactions : []));
    txns.sort((a, b) => {
      const ad = String(a?.date || '');
      const bd = String(b?.date || '');
      if (ad !== bd) return ad.localeCompare(bd);
      const as = Number(a?.statementId || 0);
      const bs = Number(b?.statementId || 0);
      if (as !== bs) return as - bs;
      return Number(a?.rowIndex || 0) - Number(b?.rowIndex || 0);
    });
    return {
      id: null,
      name: selectedStatements.length === 1 ? (selectedStatements[0]?.name || `Statement #${selectedStatements[0]?.id || ''}`) : `Selected statements (${selectedStatements.length})`,
      transactions: txns
    };
  }, [selectedStatements]);

  const filteredListData = useMemo(() => {
    if (!reimbToFilter) return Array.isArray(data) ? data : [];
    return (Array.isArray(data) ? data : []).filter((exp) => {
      if (!exp?.isReimbursement) return false;
      const identity = getExpenseFiledForIdentity(exp);
      if (reimbToFilter.filedForType === 'EMPLOYEE') {
        return identity.filedForType === 'EMPLOYEE'
          && Number(identity.filedForEmployeeId || 0) === Number(reimbToFilter.filedForEmployeeId || 0);
      }
      return identity.filedForType === 'USER'
        && Number(identity.userId || 0) === Number(reimbToFilter.userId || 0);
    });
  }, [data, reimbToFilter]);

  const totalPages = Math.ceil(total / pageSize) || 1;
  const visibleTotalPages = reimbToFilter ? 1 : totalPages;

  const calendarMonthStart = useMemo(
    () => new Date(calendarCursor.getFullYear(), calendarCursor.getMonth(), 1),
    [calendarCursor]
  );
  const calendarGridStart = useMemo(() => {
    const d = new Date(calendarMonthStart);
    d.setDate(d.getDate() - d.getDay());
    return d;
  }, [calendarMonthStart]);
  const calendarDays = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(calendarGridStart);
      d.setDate(calendarGridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [calendarGridStart]);
  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const calendarByDay = useMemo(() => {
    const map = {};
    (Array.isArray(calendarData) ? calendarData : []).forEach((row) => {
      const key = String(row?.date || '').slice(0, 10);
      if (!key) return;
      // Keep calendar focused on actionable timeline:
      // projected + upcoming unpaid + paid (exclude rejected).
      if (!row?.isProjected && String(row?.status || '').toUpperCase() === 'REJECTED') return;
      if (!map[key]) map[key] = [];
      map[key].push(row);
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
    });
    return map;
  }, [calendarData]);
  const selectedDayItems = useMemo(
    () => (calendarSelectedYmd ? (calendarByDay[calendarSelectedYmd] || []) : []),
    [calendarByDay, calendarSelectedYmd]
  );
  const getCalendarPaymentMeta = (item) => {
    if (item?.isProjected) return null;
    const itemCurrency = String(item?.currency || 'INR').toUpperCase();
    const totalValue = (() => {
      const n = toMoneyNumber(item?.total);
      return Number.isFinite(n) ? n : Number(item?.total || 0);
    })();
    const convertedValue = toMoneyNumber(item?.convertedAmount);
    const totalInrValue = itemCurrency !== 'INR' && Number.isFinite(convertedValue)
      ? convertedValue
      : totalValue;
    const vendorPaid = toMoneyNumber(item?.vendorPaidTotal);
    const reimbPaid = toMoneyNumber(item?.reimbursedTotal);
    const paidRaw = item?.isReimbursement ? reimbPaid : vendorPaid;
    const paidValue = Number.isFinite(paidRaw)
      ? (
        itemCurrency !== 'INR'
          && !item?.isReimbursement
          && Number.isFinite(convertedValue)
          && paidRaw > 0
          && paidRaw <= (totalValue + 1)
          ? convertedValue
          : paidRaw
      )
      : (String(item?.status || '').toUpperCase() === 'PAID' ? totalInrValue : 0);
    const dueValue = Math.max(0, totalInrValue - paidValue);
    return {
      paidValue: Math.max(0, paidValue),
      dueValue,
      isPaid: String(item?.status || '').toUpperCase() === 'PAID' || dueValue <= 0
    };
  };
  const getCalendarItemKind = (item) => {
    if (item?.isProjected) return 'PROJECTED';
    const paymentMeta = getCalendarPaymentMeta(item);
    return paymentMeta?.isPaid ? 'PAID' : 'DUE';
  };
  const selectedDayTotals = useMemo(() => {
    return selectedDayItems.reduce((acc, item) => {
      if (item?.isProjected) {
        acc.due += Number(item?.total || 0);
        return acc;
      }
      const paymentMeta = getCalendarPaymentMeta(item);
      if (!paymentMeta) return acc;
      acc.paid += Number(paymentMeta.paidValue || 0);
      acc.due += Number(paymentMeta.dueValue || 0);
      return acc;
    }, { paid: 0, due: 0 });
  }, [selectedDayItems]);
  const monthTotals = useMemo(() => {
    return (Array.isArray(calendarData) ? calendarData : []).reduce((acc, item) => {
      if (item?.isProjected) {
        acc.due += Number(item?.total || 0);
        return acc;
      }
      const paymentMeta = getCalendarPaymentMeta(item);
      if (!paymentMeta) return acc;
      acc.paid += Number(paymentMeta.paidValue || 0);
      acc.due += Number(paymentMeta.dueValue || 0);
      return acc;
    }, { paid: 0, due: 0 });
  }, [calendarData]);

  const gotoCalendarMonth = (offset) => {
    const next = new Date(calendarCursor.getFullYear(), calendarCursor.getMonth() + offset, 1);
    setCalendarCursor(next);
    const r = monthRange(next);
    setDateRange({
      startDate: r.startDate.toISOString(),
      endDate: r.endDate.toISOString(),
      label: next.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    });
  };

  const switchToCalendar = () => {
    setViewMode('calendar');
    setPage(1);
    const source = (dateRange && dateRange !== 'all' && dateRange.startDate)
      ? new Date(dateRange.startDate)
      : new Date();
    const base = Number.isNaN(source.getTime()) ? new Date() : source;
    const next = new Date(base.getFullYear(), base.getMonth(), 1);
    setCalendarCursor(next);
    const r = monthRange(next);
    setDateRange({
      startDate: r.startDate.toISOString(),
      endDate: r.endDate.toISOString(),
      label: next.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    });
  };

  // Keep calendar month selector and effective range in sync.
  useEffect(() => {
    if (viewMode !== 'calendar') return;
    if (!dateRange || dateRange === 'all' || !dateRange.startDate) return;
    const start = new Date(dateRange.startDate);
    if (Number.isNaN(start.getTime())) return;
    const sy = start.getFullYear();
    const sm = start.getMonth();
    if (calendarCursor.getFullYear() === sy && calendarCursor.getMonth() === sm) return;
    setCalendarCursor(new Date(sy, sm, 1));
  }, [viewMode, dateRange, calendarCursor]);

  const setCalendarMonthYear = (nextMonth, nextYear) => {
    const month = Number(nextMonth);
    const year = Number(nextYear);
    if (!Number.isInteger(month) || month < 0 || month > 11) return;
    if (!Number.isInteger(year) || year < 1900 || year > 3000) return;
    const next = new Date(year, month, 1);
    setCalendarCursor(next);
    const r = monthRange(next);
    setDateRange({
      startDate: r.startDate.toISOString(),
      endDate: r.endDate.toISOString(),
      label: next.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    });
  };
  const calendarYearOptions = useMemo(() => {
    const center = calendarCursor.getFullYear();
    return Array.from({ length: 15 }, (_, i) => center - 7 + i);
  }, [calendarCursor]);

  const displayOrDash = (val) => {
    if (val === null || val === undefined) return '-';
    const s = String(val).trim();
    if (!s) return '-';
    const lowered = s.toLowerCase();
    if (['null', 'na', 'n/a', 'none', '-'].includes(lowered)) return '-';
    return s;
  };

  const openReview = async (exp) => {
    const id = exp?.id;
    if (!id) return;
    setDrawerOpen(true);
    setReviewId(id);
    setReview(exp || null); // optimistic from table row
    setReviewLoading(true);
    try {
      const full = await ExpensesAPI.getById(id);
      setReview(full);
    } catch (e) {
      console.error(e);
    }
    setReviewLoading(false);
  };

  const closeReview = () => {
    setDrawerOpen(false);
    setReviewId(null);
    setReview(null);
    setReviewLoading(false);
  };

  const openPaidModal = (exp) => {
    if (!exp?.id) return;
    setPaidModalExp(exp);
    const isReimb = Boolean(exp?.isReimbursement);
    const expCurrency = String(exp?.currency || 'INR').toUpperCase();
    const totalValue = (() => {
      const n = toMoneyNumber(exp?.total);
      return Number.isFinite(n) ? n : Number(exp?.total || 0);
    })();
    const convertedValue = toMoneyNumber(exp?.convertedAmount);
    const totalInrValue = expCurrency !== 'INR' && Number.isFinite(convertedValue)
      ? convertedValue
      : totalValue;
    const latestVendor = latestVendorPayment(exp);
    const latestReimb = latestReimbursement(exp);
    const defaultAmt = isReimb
      ? (exp?.reimbursedTotal ?? totalInrValue ?? exp?.total ?? '')
      : (exp?.vendorPaidTotal ?? exp?.total ?? '');
    setPaidAmount(String(defaultAmt ?? ''));
    setPaidReason(String(isReimb ? (latestReimb?.reason || '') : (latestVendor?.note || '')));
    setPaidModalOpen(true);
  };

  const closePaidModal = () => {
    if (paidSaving) return;
    setPaidModalOpen(false);
    setPaidModalExp(null);
    setPaidAmount('');
    setPaidReason('');
  };

  const closeVendorModal = () => {
    setVendorModalOpen(false);
    setVendorModalLoading(false);
    setVendorModalVendor(null);
    setVendorBillsLoading(false);
    setVendorBills([]);
    setVendorBillsTotal(0);
  };

  const openVendorModal = async (vendorId, optimisticVendor) => {
    if (!vendorId) return;
    setVendorModalOpen(true);
    setVendorModalVendor(optimisticVendor || null);
    setVendorModalLoading(true);
    setVendorBillsLoading(true);
    try {
      const v = await VendorsAPI.getById(vendorId);
      setVendorModalVendor(v);
    } catch (e) {
      // keep optimistic
    }
    setVendorModalLoading(false);
    try {
      const resp = await ExpensesAPI.list({ vendorId, page: 1, pageSize: 10 });
      setVendorBills(Array.isArray(resp?.data) ? resp.data : []);
      setVendorBillsTotal(Number(resp?.total || 0));
    } catch (e) {
      setVendorBills([]);
      setVendorBillsTotal(0);
    }
    setVendorBillsLoading(false);
  };

  const reviewTax = useMemo(() => {
    const r = review || {};
    return Number(r.cgstAmount || 0) + Number(r.sgstAmount || 0) + Number(r.igstAmount || 0) + Number(r.cessAmount || 0);
  }, [review]);
  const reviewLatestVendorPayment = useMemo(() => latestVendorPayment(review), [review]);
  const reviewLatestReimbursement = useMemo(() => latestReimbursement(review), [review]);
  const reviewPaymentActor = useMemo(() => paymentActor(review), [review]);
  const reviewPaidByName = useMemo(() => paymentPaidName(review), [review]);

  const handleAction = async (e, action, id) => {
    e.stopPropagation();
    try {
      if (action === 'approve') await ExpensesAPI.approve(id);
      else if (action === 'reject') {
        const reason = window.prompt('Rejection reason (optional):');
        if (reason === null) return; // user cancelled
        await ExpensesAPI.reject(id, { notes: reason });
      }
      else if (action === 'markPaid') {
        const exp = data.find((x) => x.id === id);
        openPaidModal(exp);
        return; // modal submit will reload
      }
      await load();
      // If the review drawer is open for this expense, refresh it.
      if (drawerOpen && Number(reviewId) === Number(id)) {
        try {
          const full = await ExpensesAPI.getById(id);
          setReview(full);
        } catch (_) { /* ignore */ }
      }
    } catch (err) { alert(err?.response?.data?.error || 'Action failed'); }
  };

  return (
    <div className="page-content">
      <style>{billingTableCSS()}</style>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Expenses</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <div style={styles.searchBox}>
              <FiSearch size={14} color={colors.stone400} />
              <input
                style={styles.searchInput}
                list={vendorDatalistId}
                placeholder="Vendor…"
                value={vendor}
                onChange={(e) => { setVendor(e.target.value); setPage(1); }}
              />
              <datalist id={vendorDatalistId}>
                {vendorOptions.map((v) => (
                  <option key={v.id} value={v.name} />
                ))}
              </datalist>
            </div>
          </div>
          {viewMode === 'calendar' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <select
                style={styles.monthYearSelect}
                value={calendarCursor.getMonth()}
                onChange={(e) => { setCalendarMonthYear(Number(e.target.value), calendarCursor.getFullYear()); setPage(1); }}
                title="Month"
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={name} value={idx}>{name}</option>
                ))}
              </select>
              <select
                style={styles.monthYearSelect}
                value={calendarCursor.getFullYear()}
                onChange={(e) => { setCalendarMonthYear(calendarCursor.getMonth(), Number(e.target.value)); setPage(1); }}
                title="Year"
              >
                {calendarYearOptions.map((yy) => (
                  <option key={yy} value={yy}>{yy}</option>
                ))}
              </select>
            </div>
          ) : (
            <HeaderDateFilters
              value={dateRange}
              onChange={(range) => { setDateRange(range); setPage(1); }}
            />
          )}
          {viewMode !== 'calendar' && (statusFilter || categoryFilter || vendor || reimbToFilter) && (
            <button
              type="button"
              className="btn btn-secondary"
              style={styles.smallBtn}
              onClick={() => { setStatusFilter(''); setCategoryFilter(''); setVendor(''); setReimbToFilter(null); setDateRange('all'); setPage(1); }}
            >
              Reset filters
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            style={styles.smallBtn}
            onClick={handleExportCsv}
            disabled={exporting || loading}
            title="Export expenses as CSV"
          >
            {exporting ? <FiRefreshCw size={12} /> : <FiExternalLink size={12} />} {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={styles.smallBtn}
            onClick={() => navigate('/expense/new?mode=recurring')}
          >
            <FiRepeat size={12} /> New Recurring
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={styles.smallBtn}
            onClick={() => setBulkModalOpen(true)}
          >
            Bulk Upload
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={styles.smallBtn}
            onClick={() => setStatementModalOpen(true)}
          >
            Upload Statement
          </button>
          <button className="submit-button" style={styles.addBtn} onClick={() => navigate('/expense/new')}>
            <FiPlus size={16} /> New Expense
          </button>
        </div>
      </div>

      {/* Statement matching section */}
      <div style={{ marginBottom: 16, border: `1px solid ${colors.stone200}`, borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
        <div style={{ padding: 12, borderBottom: `1px solid ${colors.stone200}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: colors.stone800 }}>Statements</div>
            <div style={{ fontSize: 12, color: colors.stone500 }}>Upload statements and confirm transaction matches as payment proof.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {selectedStatementIds.length > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                style={styles.smallBtn}
                onClick={() => setSelectedStatementIds([])}
              >
                Clear selection
              </button>
            )}
            <button type="button" className="btn btn-secondary" style={styles.smallBtn} onClick={loadStatements}>
              <FiRefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>
        <div style={{ padding: 12 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {statements.length === 0 && (
              <div style={{ fontSize: 12, color: colors.stone500 }}>No statements uploaded yet.</div>
            )}
            {statements.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelectedStatementIds((prev) => {
                  const arr = Array.isArray(prev) ? prev : [];
                  const id = Number(s.id);
                  if (!id) return arr;
                  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
                })}
                style={{
                  border: `1px solid ${selectedStatementIds.includes(s.id) ? colors.stone800 : colors.stone200}`,
                  borderRadius: 8,
                  background: selectedStatementIds.includes(s.id) ? colors.stone100 : '#fff',
                  padding: '6px 10px',
                  fontSize: 12,
                  color: colors.stone800,
                  cursor: 'pointer'
                }}
              >
                {bankShortcode(s.bankName)} - {s.accountNumber} 
                {statementPeriod(s) ? ` • ${statementPeriod(s)}` : ''} • {s.status || 'UPLOADED'}
              </button>
            ))}
          </div>
          {selectedStatementIds.length > 0 && (
            <MatchReviewPanel
              statement={combinedStatement}
              statementById={selectedStatementsById}
              expenseOptions={data}
              companyName={company?.name || ''}
              matchFilter={matchFilter}
              onMatchFilterChange={setMatchFilter}
              onRerunMatching={async () => {
                const ids = Array.isArray(selectedStatementIds) ? selectedStatementIds : [];
                for (const id of ids) {
                  // eslint-disable-next-line no-await-in-loop
                  await StatementsAPI.runMatch(id);
                  // eslint-disable-next-line no-await-in-loop
                  const refreshed = await fetchStatementByIdDedupe(id);
                  setSelectedStatementsById((prev) => ({ ...(prev || {}), [id]: refreshed }));
                }
              }}
              onUpdated={(next) => {
                load();
                loadStatements();
                if (next?.id) {
                  setSelectedStatementsById((prev) => ({ ...(prev || {}), [next.id]: next }));
                }
              }}
              onViewExpense={(expense) => {
                if (!expense?.id) return;
                openReview(expense);
              }}
            />
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <div
          style={{ ...styles.statCard, ...styles.statCardClickable(!statusFilter && !categoryFilter && !vendor && !reimbToFilter && (dateRange === 'all' || !dateRange)) }}
          onClick={() => { setStatusFilter(''); setPage(1); }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
          role="button"
          tabIndex={0}
        >
          <div style={styles.statLabel}>Total Expenses</div>
          <div style={styles.statValue}>{fmt(stats.totalAmount)}</div>
        </div>
        {['DRAFT', 'PENDING', 'APPROVED', 'PAID', 'REJECTED'].map((s) => {
          const d = stats.byStatus?.[s] || {};
          return (
            <div
              key={s}
              style={{ ...styles.statCard, ...styles.statCardClickable(statusFilter === s) }}
              onClick={() => { setStatusFilter(statusFilter === s ? '' : s); setPage(1); }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
              role="button"
              tabIndex={0}
            >
              <div style={styles.statLabel}>{s.charAt(0) + s.slice(1).toLowerCase()}</div>
              <div style={{ ...styles.statValue, fontSize: 18 }}>{d.count || 0}</div>
              <div style={{ fontSize: 12, color: colors.stone400 }}>{fmt(d.amount)}</div>
            </div>
          );
        })}
      </div>

      {/* Category graph (Invoices-like segmented bar + legend) */}
      {stats.byCategory && Object.keys(stats.byCategory).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {(() => {
            const entries = Object.entries(stats.byCategory || {}).map(([k, d]) => ({
              key: k,
              label: CATEGORY_LABELS[k] || k,
              amount: Number(d?.amount || 0),
              count: Number(d?.count || 0),
              color: CATEGORY_COLORS[k] || matte.grey
            })).filter((x) => x.amount > 0);
            const totalAmt = entries.reduce((a, b) => a + b.amount, 0) || 1;
            const sorted = entries.sort((a, b) => b.amount - a.amount);
            return (
              <>
                <div style={styles.segBar} aria-hidden="true">
                  {sorted.map((it) => (
                    <div
                      key={it.key}
                      style={{ width: `${(it.amount / totalAmt) * 100}%`, background: it.color }}
                      title={`${it.label}: ${fmt(it.amount)}`}
                    />
                  ))}
                </div>
                <div style={styles.legendGrid}>
                  {sorted.slice(0, 8).map((it) => (
                    <div key={it.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.stone700, padding: '2px 0' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: it.color }} />
                      <span style={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {it.label} <span style={{ color: colors.stone500 }}>({it.count})</span>
                      </span>
                      <span style={{ color: colors.stone500 }}>•</span>
                      <span style={{ color: colors.stone800, fontWeight: 600 }}>{fmt(it.amount)}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Admin: Reimbursement-to breakdown (same style as Category chart) */}
      {isAdmin && Array.isArray(stats.reimbTo) && stats.reimbTo.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {(() => {
            const palette = [
              matte.blue, matte.purple, matte.teal, matte.green, matte.amber, matte.red, matte.grey,
              colors.violet600, colors.blue600, colors.emerald600
            ];
            const entries = (stats.reimbTo || []).map((r, idx) => {
              const type = String(r?.filedForType || 'USER').toUpperCase();
              const p = type === 'EMPLOYEE' ? r.employee : r.user;
              const label = p ? `${[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email || ''}`.trim() : 'Unknown';
              const id = type === 'EMPLOYEE' ? (r.employeeId || `emp-${idx}`) : (r.userId || `user-${idx}`);
              return {
                key: `${type}:${id}`,
                label,
                amount: Number(r?.amount || 0),
                count: Number(r?.count || 0),
                filedForType: type,
                userId: type === 'USER' ? (Number(r?.userId || 0) || null) : null,
                filedForEmployeeId: type === 'EMPLOYEE' ? (Number(r?.employeeId || 0) || null) : null,
                color: palette[idx % palette.length]
              };
            }).filter((x) => x.amount > 0);

            const totalAmt = entries.reduce((a, b) => a + b.amount, 0) || 1;
            const sorted = entries.sort((a, b) => b.amount - a.amount);
            return (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors.stone600, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>
                  Reimbursement to
                </div>
                <div style={styles.segBar} aria-hidden="true">
                  {sorted.map((it) => (
                    <div
                      key={it.key}
                      style={{ width: `${(it.amount / totalAmt) * 100}%`, background: it.color }}
                      title={`${it.label}: ${fmt(it.amount)}`}
                    />
                  ))}
                </div>
                <div style={styles.legendGrid}>
                  {sorted.slice(0, 10).map((it) => (
                    <button
                      key={it.key}
                      type="button"
                      onClick={() => {
                        const active = reimbToFilter
                          && reimbToFilter.filedForType === it.filedForType
                          && Number(reimbToFilter.userId || 0) === Number(it.userId || 0)
                          && Number(reimbToFilter.filedForEmployeeId || 0) === Number(it.filedForEmployeeId || 0);
                        setReimbToFilter(active ? null : {
                          filedForType: it.filedForType,
                          userId: it.userId,
                          filedForEmployeeId: it.filedForEmployeeId,
                          label: it.label
                        });
                        setPage(1);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 12,
                        color: colors.stone700,
                        padding: '4px 8px',
                        borderRadius: 8,
                        border: `1px solid ${
                          reimbToFilter
                          && reimbToFilter.filedForType === it.filedForType
                          && Number(reimbToFilter.userId || 0) === Number(it.userId || 0)
                          && Number(reimbToFilter.filedForEmployeeId || 0) === Number(it.filedForEmployeeId || 0)
                            ? colors.stone800
                            : colors.stone200
                        }`,
                        background: '#fff',
                        cursor: 'pointer'
                      }}
                      title={`Filter by ${it.label}`}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: it.color }} />
                      <span style={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {it.label} <span style={{ color: colors.stone500 }}>({it.count})</span>
                      </span>
                      <span style={{ color: colors.stone500 }}>•</span>
                      <span style={{ color: colors.stone800, fontWeight: 600 }}>{fmt(it.amount)}</span>
                    </button>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {reimbToFilter && (
        <div style={{ marginBottom: 10, fontSize: 12, color: colors.stone600 }}>
          Reimbursement filter: <span style={{ fontWeight: 700, color: colors.stone800 }}>{reimbToFilter.label || 'Selected recipient'}</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          style={styles.viewToggleBtn(viewMode === 'list')}
          onClick={() => setViewMode('list')}
          title="List view"
        >
          <FiList size={14} /> List
        </button>
        <button
          type="button"
          style={styles.viewToggleBtn(viewMode === 'calendar')}
          onClick={switchToCalendar}
          title="Calendar view"
        >
          <FiCalendar size={14} /> Calendar
        </button>
      </div>

      {/* Data View */}
      {viewMode === 'calendar' ? (
        <div style={styles.calendarWrap}>
          <div style={styles.calendarCard}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <button className="btn btn-secondary" style={styles.smallBtn} onClick={() => gotoCalendarMonth(-1)}>
                <FiChevronLeft size={14} /> Prev
              </button>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.stone800 }}>
                {calendarMonthStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </div>
              <button className="btn btn-secondary" style={styles.smallBtn} onClick={() => gotoCalendarMonth(1)}>
                Next <FiChevronRight size={14} />
              </button>
            </div>
            <div style={{ fontSize: 12, color: colors.stone600, marginBottom: 10 }}>
              Month Paid: <span style={{ color: colors.emerald600, fontWeight: 700 }}>{fmt(monthTotals.paid)}</span>
              {'  '}|{'  '}
              Month Due: <span style={{ color: colors.amber600, fontWeight: 700 }}>{fmt(monthTotals.due)}</span>
            </div>
            <div style={styles.calendarGrid}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((wd) => (
                <div key={wd} style={{ fontSize: 11, fontWeight: 700, color: colors.stone500, textTransform: 'uppercase', textAlign: 'center' }}>{wd}</div>
              ))}
              {calendarDays.map((d) => {
                const key = dayKey(d);
                const items = calendarByDay[key] || [];
                const inMonth = d.getMonth() === calendarMonthStart.getMonth();
                const now = new Date();
                const isToday = key === dayKey(now);
                const isSelected = key === calendarSelectedYmd;
                return (
                  <div
                    key={key}
                    style={styles.dayCell(inMonth, isSelected, isToday)}
                    onClick={() => setCalendarSelectedYmd(key)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: colors.stone700 }}>{d.getDate()}</div>
                      {items.length > 0 && (
                        <div style={{ fontSize: 10, color: colors.stone500 }}>{items.length}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {items.slice(0, 3).map((item) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: colors.stone700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <span style={styles.dayDot(getCalendarItemKind(item))} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item?.isProjected ? (item?.recurrenceName || item?.description || 'Projected') : (item?.vendorName || item?.description || `Expense #${item.id}`)}
                          </span>
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div style={{ fontSize: 10, color: colors.stone500 }}>+{items.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={styles.calendarCard}>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.stone600, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }}>
              {calendarSelectedYmd ? `Details for ${calendarSelectedYmd}` : 'Select a day'}
            </div>
            {!calendarSelectedYmd ? (
              <div style={{ fontSize: 13, color: colors.stone500 }}>Click any day to view actual and projected expenses.</div>
            ) : selectedDayItems.length === 0 ? (
              <div style={{ fontSize: 13, color: colors.stone500 }}>No expenses for this date.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 12, color: colors.stone600 }}>
                  Paid: <span style={{ color: colors.emerald600, fontWeight: 700 }}>{fmt(selectedDayTotals.paid)}</span>
                  {'  '}|{'  '}
                  Due: <span style={{ color: colors.amber600, fontWeight: 700 }}>{fmt(selectedDayTotals.due)}</span>
                </div>
                {selectedDayItems.map((item) => (
                  <div
                    key={item.id}
                    style={{ border: `1px solid ${colors.stone200}`, borderRadius: 10, padding: 10, background: '#fff' }}
                  >
                    {(() => {
                      const paymentMeta = getCalendarPaymentMeta(item);
                      const itemKind = getCalendarItemKind(item);
                      return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: colors.stone800 }}>
                          {item?.isProjected ? (item?.recurrenceName || item?.description || 'Projected recurring expense') : (item?.description || item?.vendorName || `Expense #${item.id}`)}
                        </div>
                        <div style={{ fontSize: 11, color: colors.stone500, marginTop: 2 }}>
                          {item?.vendorName || '-'} • {CATEGORY_LABELS[item?.category] || item?.category || '-'}
                        </div>
                        {!item?.isProjected && paymentMeta && (
                          <div style={{ fontSize: 11, color: colors.stone500, marginTop: 2 }}>
                            Exp Paid {fmt(paymentMeta.paidValue)} • Exp Due {fmt(paymentMeta.dueValue)}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: colors.stone800 }}>{fmt(item?.total)}</div>
                        <span style={styles.badge(colors.stone100, itemKind === 'PROJECTED' ? colors.violet600 : (itemKind === 'PAID' ? colors.emerald600 : colors.amber600))}>
                          {itemKind === 'PROJECTED' ? 'PROJECTED' : (itemKind === 'PAID' ? 'EXP PAID' : 'EXP DUE')}
                        </span>
                      </div>
                    </div>
                      );
                    })()}
                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                      {item?.isProjected ? (
                        <button
                          type="button"
                          className="submit-button"
                          style={{ ...styles.smallBtn, height: 28 }}
                          onClick={() => navigate(`/expense/new?recurringId=${item.recurringExpenseId}&occurrenceDate=${item.occurrenceDate}`)}
                        >
                          <FiPlus size={12} /> Create Expense
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ ...styles.smallBtn, height: 28 }}
                          onClick={() => openReview(item)}
                        >
                          Open
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
      loading ? (
        <div style={styles.empty}>Loading...</div>
      ) : filteredListData.length === 0 ? (
        <div style={styles.empty}>No expenses found. Click "New Expense" to add one.</div>
      ) : (
        <div style={styles.tableWrap}>
          <table className="billing-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Vendor</th>
                <th style={styles.th}>Added by</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredListData.map((exp) => {
              const sc = STATUS_COLORS[exp.status] || STATUS_COLORS.DRAFT;
              const tax = Number(exp.cgstAmount || 0) + Number(exp.sgstAmount || 0) + Number(exp.igstAmount || 0) + Number(exp.cessAmount || 0);
              const expCurrency = String(exp?.currency || 'INR').toUpperCase();
              const totalValue = (() => {
                const n = toMoneyNumber(exp?.total);
                return Number.isFinite(n) ? n : Number(exp?.total || 0);
              })();
              const convertedValue = toMoneyNumber(exp?.convertedAmount);
              const totalInrValue = expCurrency !== 'INR' && Number.isFinite(convertedValue)
                ? convertedValue
                : totalValue;
              const vendorPaid = toMoneyNumber(exp?.vendorPaidTotal);
              const reimbPaid = toMoneyNumber(exp?.reimbursedTotal);

              // Table Paid/Due line uses reimbursement paid for reimbursements, vendor paid otherwise.
              const paidValueRaw = exp?.isReimbursement ? reimbPaid : vendorPaid;
              const paidValue = Number.isFinite(paidValueRaw)
                ? (
                  expCurrency !== 'INR'
                    && !exp?.isReimbursement
                    && Number.isFinite(convertedValue)
                    && paidValueRaw > 0
                    && paidValueRaw <= (totalValue + 1)
                    ? convertedValue
                    : paidValueRaw
                )
                : (exp.status === 'PAID' ? totalInrValue : null);

              const dueValue = (paidValue !== null && Number.isFinite(paidValue))
                ? Math.max(0, totalInrValue - paidValue)
                : null;
              const showPaidLine = exp.status !== 'REJECTED' && (paidValue !== null && Number.isFinite(paidValue));
              const showDue = exp.status !== 'PAID' && exp.status !== 'REJECTED' && dueValue !== null && dueValue > 0;

              // Fully paid for non-reimbursements (vendor payment)
              const vendorIsFullyPaid = exp.status === 'PAID' || (totalValue > 0 && Number.isFinite(vendorPaid) && vendorPaid >= totalValue);
              // Fully paid for reimbursements (payout)
              const reimbIsFullyPaid = exp.status === 'PAID' || (totalInrValue > 0 && Number.isFinite(reimbPaid) && reimbPaid >= totalInrValue);
              const addedBy = exp?.createdByUser
                ? `${[exp.createdByUser.firstName, exp.createdByUser.lastName].filter(Boolean).join(' ') || exp.createdByUser.email || ''}`.trim()
                : '';
              const filedForUser = exp?.user
                ? `${[exp.user.firstName, exp.user.lastName].filter(Boolean).join(' ') || exp.user.email || ''}`.trim()
                : '';
              const filedForEmployee = exp?.filedForEmployee
                ? `${[exp.filedForEmployee.firstName, exp.filedForEmployee.lastName].filter(Boolean).join(' ') || exp.filedForEmployee.email || ''}`.trim()
                : '';
              const filedFor = String(exp?.filedForType || '').toUpperCase() === 'EMPLOYEE'
                ? filedForEmployee
                : filedForUser;
              const statusByUser = (() => {
                if (['APPROVED', 'REJECTED'].includes(exp.status)) return exp?.approvedByUser || null;
                if (exp.status === 'PAID') return paymentActor(exp) || exp?.approvedByUser || exp?.createdByUser || null;
                return exp?.createdByUser || null;
              })();
              const statusBy = statusByUser
                ? `${[statusByUser.firstName, statusByUser.lastName].filter(Boolean).join(' ') || statusByUser.email || ''}`.trim()
                : '';
              const approverName = exp?.reimbursementApprover
                ? `${[exp.reimbursementApprover.firstName, exp.reimbursementApprover.lastName].filter(Boolean).join(' ') || exp.reimbursementApprover.email || ''}`.trim()
                : '';
              return (
                <tr key={exp.id} className="billing-row" style={styles.tr} onClick={() => openReview(exp)}>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{exp.date}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {exp?.vendor?.id ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openVendorModal(exp.vendor.id, exp.vendor); }}
                        style={{ background: 'transparent', border: 'none', padding: 0, margin: 0, cursor: 'pointer', color: colors.stone800, fontWeight: 700, textAlign: 'left' }}
                        title="View vendor"
                      >
                        {exp.vendor.name || exp.vendorName || '-'}
                      </button>
                    ) : (
                      (exp.vendor?.name || exp.vendorName || '-')
                    )}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>{addedBy || '-'}</td>
                  <td style={styles.td}>
                    <span style={styles.catDot(CATEGORY_COLORS[exp.category] || matte.grey)} />
                    {CATEGORY_LABELS[exp.category] || exp.category}
                  </td>
                  <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {exp.description || exp.number || '-'}
                    {exp.number && (
                      <div style={{ fontSize: 11, color: colors.stone500, marginTop: 4 }}>
                        Invoice #: {exp.number || '-'}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <div style={{ fontWeight: 600, color: colors.stone800 }}>{fmtCurr(exp.taxableValue, expCurrency)}</div>
                      <div style={{ fontSize: 11, color: colors.stone500 }}>
                        Tax {fmtCurr(tax, expCurrency)}
                      </div>
                      {exp?.itcEligible && (
                        <div style={{ fontSize: 11, color: colors.violet600, fontWeight: 600 }}>
                          ITC eligible
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    <div style={{ color: colors.stone800 }}>{fmtCurr(exp.total, expCurrency)}</div>
                    {expCurrency !== 'INR' && Number.isFinite(convertedValue) && convertedValue > 0 && (
                      <div style={{ fontSize: 11, color: colors.stone500, marginTop: 1, fontWeight: 400 }}>
                        {fmt(convertedValue)} INR
                      </div>
                    )}
                    {showPaidLine && (
                      <div style={{ fontSize: 11, color: colors.stone500, marginTop: 1, fontWeight: 400 }}>
                        Paid {fmt(paidValue)}{showDue ? ` • Due ${fmt(dueValue)}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.badge(sc.bg, sc.fg)}>{exp.status}</span>
                  
                    {exp.isReimbursement && (
                      <>
                        <div style={{ fontSize: 11, color: colors.stone500, marginTop: 4 }}>
                          Reimbursement to{' '}
                          {(filedFor || addedBy) ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const identity = getExpenseFiledForIdentity(exp);
                                if (!identity.label) return;
                                const active = reimbToFilter
                                  && reimbToFilter.filedForType === identity.filedForType
                                  && Number(reimbToFilter.userId || 0) === Number(identity.userId || 0)
                                  && Number(reimbToFilter.filedForEmployeeId || 0) === Number(identity.filedForEmployeeId || 0);
                                setReimbToFilter(active ? null : identity);
                                setPage(1);
                              }}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                margin: 0,
                                color: colors.blue600,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: 11
                              }}
                              title="Filter by reimbursement recipient"
                            >
                              {filedFor || addedBy}
                            </button>
                          ) : '-'}
                        </div>
                        <div style={{ fontSize: 11, color: colors.stone500, marginTop: 2 }}>
                          ({fmt((paidValue === null || !Number.isFinite(paidValue)) ? 0 : paidValue)} / {fmtCurr(exp?.total, expCurrency)})
                        </div>
                      </>
                    )}
                    {exp.status === 'PENDING' && exp.isReimbursement && approverName ? (
                      <div style={{ fontSize: 11, color: colors.stone500, marginTop: 4 }}>
                        Approver: {approverName}
                      </div>
                    ) : statusBy && (
                      <div style={{ fontSize: 11, color: colors.stone500, marginTop: 4 }}>
                        by {statusBy}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                      {(exp.isReimbursement && exp.status === 'APPROVED' && !reimbIsFullyPaid) ? (
                        <button
                          type="button"
                          style={{ ...styles.actionBtn, padding: '4px 8px' }}
                          title="Record reimbursement payout"
                          onClick={(e) => {
                            return handleAction(e, 'markPaid', exp.id);
                          }}
                        >
                          <FiRepeat size={13} color={colors.violet600} />
                        </button>
                      ) : null}
                      {['DRAFT', 'PENDING'].includes(exp.status) && (
                        <>
                          <button style={styles.actionBtn} title="Approve" onClick={(e) => handleAction(e, 'approve', exp.id)}>
                            <FiCheckCircle size={13} color={colors.emerald600} />
                          </button>
                          <button style={styles.actionBtn} title="Reject" onClick={(e) => handleAction(e, 'reject', exp.id)}>
                            <FiXCircle size={13} color={colors.rose600} />
                          </button>
                        </>
                      )}
                      {/* Pay icon is for non-reimbursements only; reimbursements use the reimburse icon above */}
                      {!exp.isReimbursement && exp.status === 'APPROVED' && !vendorIsFullyPaid && (
                        <button style={styles.actionBtn} title="Mark Paid" onClick={(e) => handleAction(e, 'markPaid', exp.id)}>
                          <FiCreditCard size={13} color={colors.blue600} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )
      )}

      {/* Pagination */}
      {viewMode === 'list' && visibleTotalPages > 1 && (
        <div style={styles.pagination}>
          <button style={styles.pageBtn(page <= 1)} onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>
            <FiChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 13, color: colors.stone500 }}>Page {page} of {visibleTotalPages}</span>
          <button style={styles.pageBtn(page >= visibleTotalPages)} onClick={() => setPage(Math.min(visibleTotalPages, page + 1))} disabled={page >= visibleTotalPages}>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Review drawer */}
      {drawerOpen && (
        <div style={styles.drawer} role="dialog" aria-modal="true" aria-label="Expense review" onClick={closeReview}>
          <div style={styles.drawerContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <h3 style={styles.drawerTitle}>
                  Expense #{review?.id || reviewId || ''}
                </h3>
                {review?.status && (() => {
                  const sc = STATUS_COLORS[review.status] || STATUS_COLORS.DRAFT;
                  return <span style={styles.badge(sc.bg, sc.fg)}>{review.status}</span>;
                })()}
                {review?.status && !['DRAFT', 'PENDING'].includes(review.status) && <span style={styles.lockPill}>Read-only</span>}
              </div>
              <div style={styles.btnRow}>
                {['DRAFT', 'PENDING'].includes(review?.status) && (
                  <button
                    className="submit-button"
                    style={{ ...styles.smallBtn, height: 25, minHeight: 25 }}
                    onClick={() => navigate(`/expense/${review.id}/edit`)}
                    disabled={!review?.id}
                  >
                    <FiEdit2 size={12} /> Edit
                  </button>
                )}
                <button className="btn btn-secondary" style={styles.smallBtn} onClick={closeReview}>
                  <FiX size={12} /> Close
                </button>
              </div>
            </div>

            <div style={styles.drawerBody}>
              {reviewLoading && (
                <div style={{ fontSize: 13, color: colors.stone500 }}>Loading…</div>
              )}

              {/* Row 1: Preview | Notes */}
              <div style={styles.twoColTight}>
                <div style={styles.cardLike}>
                  <div style={{ ...styles.sectionTitle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span>Preview</span>
                    {review?.receiptUrl && (
                      <a
                        href={review.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in new tab"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, border: `1px solid ${colors.stone200}`, background: '#fff', color: colors.stone700 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiExternalLink size={14} />
                      </a>
                    )}
                  </div>
                  {review?.receiptUrl ? (
                    <div style={styles.previewWrap}>
                      {String(review.receiptUrl).toLowerCase().endsWith('.pdf') ? (
                        <iframe title="Receipt PDF" src={review.receiptUrl} style={styles.previewPdf} />
                      ) : (
                        <a href={review.receiptUrl} target="_blank" rel="noopener noreferrer">
                          <img alt="Receipt" src={review.receiptUrl} style={styles.previewImg} />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: colors.stone500 }}>No receipt uploaded.</div>
                  )}
                </div>
                <div style={styles.cardLike}>
                  <div style={styles.sectionTitle}>Notes</div>
                  <div style={styles.noteBox}>{review?.notes || '-'}</div>
                </div>
              </div>

              <div style={{ height: 14 }} />

              {/* Row 2: Vendor | Expense */}
              <div style={styles.twoCol}>
                <div style={styles.cardLike}>
                  <div style={styles.sectionTitle}>Vendor</div>
                  <div style={styles.kv}><div style={styles.k}>Name</div><div style={styles.v}>{review?.vendor?.name || review?.vendorName || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>GSTIN</div><div style={styles.v}>{review?.vendor?.gstin || review?.vendorGstin || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Phone</div><div style={styles.v}>{review?.vendor?.phone || review?.vendorPhone || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Email</div><div style={styles.v}>{review?.vendor?.email || review?.vendorEmail || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Website</div><div style={styles.v}>{displayOrDash(review?.vendor?.website || review?.vendorWebsite)}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Address</div><div style={styles.v}>{displayOrDash(typeof (review?.vendor?.address ?? review?.vendorAddress) === 'object' ? ((review?.vendor?.address ?? review?.vendorAddress)?.full || JSON.stringify(review?.vendor?.address ?? review?.vendorAddress)) : (review?.vendor?.address ?? review?.vendorAddress))}</div></div>
                  <div style={{ height: 8 }} />
                  <div style={{ fontSize: 11, fontWeight: 800, color: colors.stone600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    Billed To
                  </div>
                  <div style={styles.kv}><div style={styles.k}>Name</div><div style={styles.v}>{review?.buyerName || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>GSTIN</div><div style={styles.v}>{review?.buyerGstin || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Phone</div><div style={styles.v}>{review?.buyerPhone || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Email</div><div style={styles.v}>{review?.buyerEmail || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Address</div><div style={styles.v}>{displayOrDash(typeof review?.buyerAddress === 'object' ? (review?.buyerAddress?.full || JSON.stringify(review?.buyerAddress)) : review?.buyerAddress)}</div></div>
                </div>
                <div style={styles.cardLike}>
                  <div style={styles.sectionTitle}>Amount</div>
                  <div style={styles.kv}><div style={styles.k}>Currency</div><div style={styles.v}>{review?.currency || 'INR'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Taxable</div><div style={styles.v}>{fmtCurr(review?.taxableValue, review?.currency)}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Tax</div><div style={styles.v}>{fmtCurr(reviewTax, review?.currency)}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Total</div><div style={styles.v}>{fmtCurr(review?.total, review?.currency)}</div></div>
                  {String(review?.currency || 'INR').toUpperCase() !== 'INR' && (
                    <>
                      <div style={styles.kv}>
                        <div style={styles.k}>Original amount</div>
                        <div style={styles.v}>
                          {Number(review?.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {String(review?.currency || '').toUpperCase()}
                        </div>
                      </div>
                      <div style={styles.kv}><div style={styles.k}>Exchange rate</div><div style={styles.v}>{review?.exchangeRate || '-'}</div></div>
                      <div style={styles.kv}><div style={styles.k}>Converted INR</div><div style={styles.v}>{review?.convertedAmount != null ? fmt(review?.convertedAmount) : '-'}</div></div>
                    </>
                  )}
                </div>
               

                
              </div>

              <div style={{ height: 14 }} />

              {/* Row 3: Amount | Payment */}
              <div style={styles.twoCol}>
               
                <div style={styles.cardLike}>
                  <div style={styles.sectionTitle}>Expense</div>
                  <div style={styles.kv}><div style={styles.k}>Date</div><div style={styles.v}>{review?.date || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Due date</div><div style={styles.v}>{review?.dueDate || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Category</div><div style={styles.v}>{CATEGORY_LABELS[review?.category] || review?.category || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Number</div><div style={styles.v}>{review?.number || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Added by</div><div style={styles.v}>{review?.createdByUser ? `${[review.createdByUser.firstName, review.createdByUser.lastName].filter(Boolean).join(' ') || review.createdByUser.email || ''}`.trim() : '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Reimbursement</div><div style={styles.v}>{review?.isReimbursement ? 'Yes' : 'No'}</div></div>
                  {review?.isReimbursement && (
                    <div style={styles.kv}>
                      <div style={styles.k}>Filed for</div>
                      <div style={styles.v}>
                        {(() => {
                          const type = String(review?.filedForType || '').toUpperCase();
                          if (type === 'EMPLOYEE') {
                            const e = review?.filedForEmployee;
                            return e ? `${[e.firstName, e.lastName].filter(Boolean).join(' ') || e.email || ''}`.trim() : '-';
                          }
                          const u = review?.user;
                          return u ? `${[u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || ''}`.trim() : '-';
                        })()}
                      </div>
                    </div>
                  )}
                  {review?.isReimbursement && (
                    <div style={styles.kv}><div style={styles.k}>Approver</div><div style={styles.v}>{review?.reimbursementApprover ? `${[review.reimbursementApprover.firstName, review.reimbursementApprover.lastName].filter(Boolean).join(' ') || review.reimbursementApprover.email || ''}`.trim() : '-'}</div></div>
                  )}
                  {review?.isReimbursement && (
                    <>
                      <div style={styles.kv}><div style={styles.k}>Approved by</div><div style={styles.v}>{review?.approvedByUser ? `${[review.approvedByUser.firstName, review.approvedByUser.lastName].filter(Boolean).join(' ') || review.approvedByUser.email || ''}`.trim() : '-'}</div></div>
                      <div style={styles.kv}><div style={styles.k}>Approved at</div><div style={styles.v}>{review?.approvedAt ? new Date(review.approvedAt).toLocaleString() : '-'}</div></div>
                      </>
                  )}
                  <div style={styles.kv}><div style={styles.k}>Description</div><div style={styles.v}>{review?.description || '-'}</div></div>
                </div>
                <div style={styles.cardLike}>
                  <div style={styles.sectionTitle}>Payment</div>
                  <div style={styles.kv}><div style={styles.k}>Method</div><div style={styles.v}>{(review?.isReimbursement ? reviewLatestReimbursement?.paymentMethod : reviewLatestVendorPayment?.paymentMethod) || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Reference</div><div style={styles.v}>{(review?.isReimbursement ? reviewLatestReimbursement?.paymentReference : reviewLatestVendorPayment?.paymentReference) || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Paid By (Name)</div><div style={styles.v}>{reviewPaidByName || '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Payee</div><div style={styles.v}>{review?.payeeName || '-'}</div></div>
                  {(() => {
                    const reviewCurrency = String(review?.currency || 'INR').toUpperCase();
                    const reviewTotal = (() => {
                      const n = toMoneyNumber(review?.total);
                      return Number.isFinite(n) ? n : Number(review?.total || 0);
                    })();
                    const reviewConverted = toMoneyNumber(review?.convertedAmount);
                    const reviewTotalInr = reviewCurrency !== 'INR' && Number.isFinite(reviewConverted)
                      ? reviewConverted
                      : reviewTotal;
                    const reviewRawPaid = toMoneyNumber(
                      review?.isReimbursement
                        ? review?.reimbursedTotal
                        : review?.vendorPaidTotal
                    );
                    const reviewPaid = Number.isFinite(reviewRawPaid)
                      ? (
                        reviewCurrency !== 'INR'
                          && !review?.isReimbursement
                          && Number.isFinite(reviewConverted)
                          && reviewRawPaid > 0
                          && reviewRawPaid <= (reviewTotal + 1)
                          ? reviewConverted
                          : reviewRawPaid
                      )
                      : (String(review?.status || '').toUpperCase() === 'PAID' ? reviewTotalInr : null);
                    const reviewDue = (reviewPaid !== null && Number.isFinite(reviewPaid))
                      ? Math.max(0, reviewTotalInr - reviewPaid)
                      : null;
                    return (
                      <>
                        <div style={styles.kv}>
                          <div style={styles.k}>Amount paid</div>
                          <div style={{ ...styles.v, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{reviewPaid !== null && Number.isFinite(reviewPaid) ? fmt(reviewPaid) : '-'}</span>
                            {review?.status === 'APPROVED' && !review?.isReimbursement && reviewDue !== null && reviewDue > 0 && (
                              <button
                                type="button"
                                style={{ ...styles.actionBtn, padding: '3px 8px', fontSize: 12 }}
                                onClick={() => openPaidModal(review)}
                                title="Mark paid"
                              >
                                <FiCreditCard size={12} color={colors.blue600} /> Pay
                              </button>
                            )}
                          </div>
                        </div>
                        <div style={styles.kv}><div style={styles.k}>Amount due</div><div style={styles.v}>{reviewDue !== null ? fmt(reviewDue) : '-'}</div></div>
                      </>
                    );
                  })()}
                  <div style={styles.kv}><div style={styles.k}>Proof</div><div style={styles.v}>{(review?.isReimbursement ? reviewLatestReimbursement?.proofUrl : reviewLatestVendorPayment?.proofUrl) ? <a href={(review?.isReimbursement ? reviewLatestReimbursement?.proofUrl : reviewLatestVendorPayment?.proofUrl)} target="_blank" rel="noreferrer">View</a> : '-'}</div></div>
                  <div style={styles.kv}><div style={styles.k}>Short paid reason</div><div style={styles.v}>{(review?.isReimbursement ? reviewLatestReimbursement?.reason : reviewLatestVendorPayment?.note) || '-'}</div></div>
                  {review?.isReimbursement && (
                    <>
                      <div style={{ height: 10 }} />
                      <div style={{ fontSize: 11, fontWeight: 800, color: colors.stone600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                        Reimbursement payout
                      </div>
                      <div style={styles.kv}><div style={styles.k}>Method</div><div style={styles.v}>{reviewLatestReimbursement?.paymentMethod || '-'}</div></div>
                      <div style={styles.kv}><div style={styles.k}>Reference</div><div style={styles.v}>{reviewLatestReimbursement?.paymentReference || '-'}</div></div>
                      <div style={styles.kv}>
                        <div style={styles.k}>Amount</div>
                        <div style={{ ...styles.v, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>{review?.reimbursedTotal !== null && review?.reimbursedTotal !== undefined ? fmt(review.reimbursedTotal) : '-'}</span>
                          {review?.status === 'APPROVED' && (() => {
                            const total = toMoneyNumber(review?.total);
                            const paid = toMoneyNumber(review?.reimbursedTotal);
                            const due = Math.max(0, (Number.isFinite(total) ? total : 0) - (Number.isFinite(paid) ? paid : 0));
                            return due > 0;
                          })() && (
                            <button
                              type="button"
                              style={{ ...styles.actionBtn, padding: '3px 8px', fontSize: 12 }}
                              onClick={() => openPaidModal(review)}
                              title="Record payout"
                            >
                              <FiCreditCard size={12} color={colors.blue600} /> Pay
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={styles.kv}><div style={styles.k}>Amount difference</div><div style={styles.v}>{(review?.total !== null && review?.total !== undefined && review?.reimbursedTotal !== null && review?.reimbursedTotal !== undefined) ? fmt(Math.max(0, Number(review.total || 0) - Number(review?.reimbursedTotal || 0))) : '-'}</div></div>
                      <div style={styles.kv}><div style={styles.k}>Proof</div><div style={styles.v}>{reviewLatestReimbursement?.proofUrl ? <a href={reviewLatestReimbursement.proofUrl} target="_blank" rel="noreferrer">View</a> : '-'}</div></div>
                      <div style={styles.kv}><div style={styles.k}>Short paid reason</div><div style={styles.v}>{reviewLatestReimbursement?.reason || '-'}</div></div>
                      <div style={styles.kv}><div style={styles.k}>Paid by</div><div style={styles.v}>{reviewPaymentActor ? `${[reviewPaymentActor.firstName, reviewPaymentActor.lastName].filter(Boolean).join(' ') || reviewPaymentActor.email || ''}`.trim() : '-'}</div></div>
                      <div style={styles.kv}><div style={styles.k}>Paid at</div><div style={styles.v}>{reviewLatestReimbursement?.paidAt ? new Date(reviewLatestReimbursement.paidAt).toLocaleString() : '-'}</div></div>
                    </>
                  )}
                </div>
              </div>

              <div style={styles.sectionTitle}>Line items</div>
              <div style={{ border: `1px solid ${colors.stone200}`, borderRadius: 10, overflow: 'auto' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.6fr 90px 70px 90px 80px 80px 1fr 90px 90px 90px 90px 90px 90px',
                    gap: 0,
                    background: colors.stone100,
                    padding: '8px 10px',
                    fontSize: 11,
                    fontWeight: 800,
                    color: colors.stone600,
                    minWidth: 1180
                  }}
                >
                  <div>Item</div>
                  <div style={{ textAlign: 'right' }}>HSN/SAC</div>
                  <div style={{ textAlign: 'right' }}>Qty</div>
                  <div style={{ textAlign: 'right' }}>Unit</div>
                  <div style={{ textAlign: 'right' }}>Price</div>
                  <div style={{ textAlign: 'right' }}>Disc</div>
                  <div style={{ textAlign: 'right' }}>Tax rates</div>
                  <div style={{ textAlign: 'right' }}>CGST</div>
                  <div style={{ textAlign: 'right' }}>SGST</div>
                  <div style={{ textAlign: 'right' }}>IGST</div>
                  <div style={{ textAlign: 'right' }}>Cess</div>
                  <div style={{ textAlign: 'right' }}>Taxable</div>
                  <div style={{ textAlign: 'right' }}>Total</div>
                </div>

                {(Array.isArray(review?.items) ? review.items : []).map((it, idx) => {
                  const discA = Number(it?.discountAmount || 0);
                  const discP = Number(it?.discountPercent || 0);
                  const discText = discA > 0 && discP > 0
                    ? `${fmt(discA)} + ${discP}%`
                    : discA > 0
                      ? fmt(discA)
                      : discP > 0
                        ? `${discP}%`
                        : '-';
                  const rates = [
                    Number(it?.cgstRate || 0) ? `C${Number(it.cgstRate)}%` : null,
                    Number(it?.sgstRate || 0) ? `S${Number(it.sgstRate)}%` : null,
                    Number(it?.igstRate || 0) ? `I${Number(it.igstRate)}%` : null,
                    Number(it?.cessRate || 0) ? `Ce${Number(it.cessRate)}%` : null
                  ].filter(Boolean).join(' ') || '-';

                  return (
                    <div
                      key={idx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1.6fr 90px 70px 90px 80px 80px 1fr 90px 90px 90px 90px 90px 90px',
                        padding: '10px 10px',
                        borderTop: `1px solid ${colors.stone200}`,
                        fontSize: 12,
                        minWidth: 1180,
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ color: colors.stone800, fontWeight: 700, minWidth: 0 }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {it?.name || it?.description || `Item ${idx + 1}`}
                        </div>
                        {!!it?.description && (
                          <div style={{ fontSize: 11, color: colors.stone500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {it.description}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{it?.hsnsac || '-'}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{Number(it?.quantity || 0) || '-'}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{it?.unit || '-'}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{fmt(it?.price)}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{discText}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{rates}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{fmt(it?.cgstAmount)}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{fmt(it?.sgstAmount)}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{fmt(it?.igstAmount)}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{fmt(it?.cessAmount)}</div>
                      <div style={{ textAlign: 'right', color: colors.stone700 }}>{fmt(it?.taxableValue)}</div>
                      <div style={{ textAlign: 'right', color: colors.stone800, fontWeight: 800 }}>{fmt(it?.lineTotal)}</div>
                    </div>
                  );
                })}
                {(!review?.items || review.items.length === 0) && (
                  <div style={{ padding: 10, fontSize: 13, color: colors.stone500 }}>No line items.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <PaymentModal
        isOpen={paidModalOpen}
        onClose={closePaidModal}
        title={paidModalExp?.isReimbursement ? 'Record reimbursement payout' : 'Mark as paid'}
        submitText="Save"
        submitLoadingText="Saving..."
        isSubmitting={paidSaving}
        acceptPdfProof={false}
        initialData={{
          paymentMethod: paidModalExp?.isReimbursement
            ? (latestReimbursement(paidModalExp)?.paymentMethod || '')
            : (latestVendorPayment(paidModalExp)?.paymentMethod || ''),
          paymentReference: paidModalExp?.isReimbursement
            ? (latestReimbursement(paidModalExp)?.paymentReference || '')
            : (latestVendorPayment(paidModalExp)?.paymentReference || ''),
          paymentDate: new Date().toISOString().slice(0, 10),
          proofUrl: paidModalExp?.isReimbursement
            ? (latestReimbursement(paidModalExp)?.proofUrl || '')
            : (latestVendorPayment(paidModalExp)?.proofUrl || '')
        }}
        additionalFields={(
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: colors.stone500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {paidModalExp?.isReimbursement ? 'Amount reimbursed' : 'Amount paid'}
            </div>
            <input
              type="number"
              min="0"
              step="any"
              style={{ width: '100%', padding: '8px 10px', fontSize: 12, border: `1.5px solid ${colors.stone200}`, borderRadius: 8, background: colors.stone50, color: colors.stone800, outline: 'none', boxSizing: 'border-box' }}
              value={paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
              placeholder="Enter amount"
              disabled={paidSaving}
            />

            {(() => {
              const total = Number(paidModalExp?.total || 0);
              const amt = Number(paidAmount || 0);
              const needsReason = total > 0 && Number.isFinite(amt) && amt > 0 && amt < total;
              if (!needsReason) return null;
              return (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: colors.stone500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                    Reason (required)
                  </div>
                  <textarea
                    style={{ width: '100%', padding: '8px 10px', fontSize: 12, border: `1.5px solid ${colors.stone200}`, borderRadius: 8, background: '#fff', color: colors.stone800, outline: 'none', boxSizing: 'border-box', minHeight: 64, resize: 'vertical' }}
                    value={paidReason}
                    onChange={(e) => setPaidReason(e.target.value)}
                    placeholder="Why is the amount less than total?"
                    disabled={paidSaving}
                  />
                </div>
              );
            })()}
          </div>
        )}
        onSubmit={async (paymentData) => {
          if (!paidModalExp?.id) return;
          setPaidSaving(true);
          try {
            const isReimb = Boolean(paidModalExp?.isReimbursement);
            const total = Number(paidModalExp?.total || 0);
            const amt = Number(paidAmount || 0);
            if (total > 0 && Number.isFinite(amt) && amt > 0 && amt < total && !String(paidReason || '').trim()) {
              alert('Reason is required when amount paid is less than total.');
              setPaidSaving(false);
              return;
            }
            await ExpensesAPI.markPaid(paidModalExp.id, {
              paymentMethod: paymentData.paymentMethod,
              paymentReference: paymentData.paymentReference,
              paymentDate: paymentData.paymentDate,
              paymentProofUrl: paymentData.paymentProofUrl,
              amountPaid: String(paidAmount || '').trim() !== '' ? Number(paidAmount) : undefined,
              shortReason: String(paidReason || '').trim() || undefined
            });
            closePaidModal();
            load();
          } catch (err) {
            alert(err?.response?.data?.error || 'Action failed');
          }
          setPaidSaving(false);
        }}
      />

      {/* Vendor details + previous bills */}
      {vendorModalOpen && (
        <div style={styles.drawer} role="dialog" aria-modal="true" aria-label="Vendor details" onClick={closeVendorModal}>
          <div
            style={{ width: 860, maxWidth: '94vw', maxHeight: '86vh', overflow: 'auto', background: '#fff', borderRadius: 14, margin: '6vh auto', padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: colors.stone800 }}>
                Vendor details
              </div>
              <button className="btn btn-secondary" style={styles.smallBtn} onClick={closeVendorModal}>
                <FiX size={12} /> Close
              </button>
            </div>

            <div style={{ marginTop: 12, border: `1px solid ${colors.stone200}`, borderRadius: 12, padding: 14, background: '#fff' }}>
              {vendorModalLoading && (
                <div style={{ fontSize: 13, color: colors.stone500 }}>Loading vendor…</div>
              )}
              {!vendorModalLoading && (
                <>
                  <div style={{ fontSize: 15, fontWeight: 800, color: colors.stone800 }}>
                    {vendorModalVendor?.name || '-'}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, marginTop: 10, fontSize: 13 }}>
                    <div style={{ color: colors.stone500 }}>GSTIN</div><div style={{ color: colors.stone800, fontWeight: 600 }}>{vendorModalVendor?.gstin || '-'}</div>
                    <div style={{ color: colors.stone500 }}>Phone</div><div style={{ color: colors.stone800, fontWeight: 600 }}>{vendorModalVendor?.phone || '-'}</div>
                    <div style={{ color: colors.stone500 }}>Email</div><div style={{ color: colors.stone800, fontWeight: 600 }}>{vendorModalVendor?.email || '-'}</div>
                    <div style={{ color: colors.stone500 }}>Website</div><div style={{ color: colors.stone800, fontWeight: 600 }}>{vendorModalVendor?.website ? <a href={vendorModalVendor.website} target="_blank" rel="noreferrer">{vendorModalVendor.website}</a> : '-'}</div>
                    <div style={{ color: colors.stone500 }}>Address</div><div style={{ color: colors.stone800, fontWeight: 600, overflowWrap: 'anywhere' }}>{displayOrDash(typeof vendorModalVendor?.address === 'object' ? (vendorModalVendor?.address?.full || JSON.stringify(vendorModalVendor.address)) : vendorModalVendor?.address)}</div>
                    <div style={{ color: colors.stone500 }}>Last used</div><div style={{ color: colors.stone800, fontWeight: 600 }}>{vendorModalVendor?.lastUsedAt ? new Date(vendorModalVendor.lastUsedAt).toLocaleString() : '-'}</div>
                  </div>
                </>
              )}
            </div>

            <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: colors.stone600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Previous bills
              </div>
              <div style={{ fontSize: 12, color: colors.stone500 }}>
                {vendorBillsTotal ? `${vendorBillsTotal} total` : ''}
              </div>
            </div>

            <div style={{ marginTop: 10, border: `1px solid ${colors.stone200}`, borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '110px 160px 1fr 120px 120px', background: colors.stone100, padding: '8px 12px', fontSize: 11, fontWeight: 800, color: colors.stone600 }}>
                <div>Date</div>
                <div>Number</div>
                <div>Description</div>
                <div style={{ textAlign: 'right' }}>Total</div>
                <div style={{ textAlign: 'right' }}>Status</div>
              </div>
              {vendorBillsLoading ? (
                <div style={{ padding: 12, fontSize: 13, color: colors.stone500 }}>Loading bills…</div>
              ) : vendorBills.length === 0 ? (
                <div style={{ padding: 12, fontSize: 13, color: colors.stone500 }}>No bills found for this vendor.</div>
              ) : (
                vendorBills.map((b) => {
                  const sc = STATUS_COLORS[b.status] || STATUS_COLORS.DRAFT;
                  return (
                    <div
                      key={b.id}
                      style={{ display: 'grid', gridTemplateColumns: '110px 160px 1fr 120px 120px', padding: '10px 12px', borderTop: `1px solid ${colors.stone200}`, fontSize: 13, cursor: 'pointer' }}
                      onClick={() => { closeVendorModal(); openReview(b); }}
                    >
                      <div style={{ color: colors.stone700 }}>{b.date || '-'}</div>
                      <div style={{ color: colors.stone700 }}>{b.number || '-'}</div>
                      <div style={{ color: colors.stone800, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.description || '-'}</div>
                      <div style={{ textAlign: 'right', color: colors.stone800, fontWeight: 700 }}>{fmt(b.total)}</div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={styles.badge(sc.bg, sc.fg)}>{b.status}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <BulkUploadModal
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        onCreated={(result) => {
          // Keep modal open if duplicates exist so user can review/view them.
          if (!result?.duplicates) setBulkModalOpen(false);
          load();
          loadStatements();
        }}
        onViewExpense={(expenseId) => {
          if (!expenseId) return;
          // Close overlay so review drawer remains visible.
          setBulkModalOpen(false);
          openReview({ id: expenseId });
        }}
      />

      <StatementUploadModal
        isOpen={statementModalOpen}
        onClose={() => setStatementModalOpen(false)}
        onUploaded={(uploaded) => {
          if (uploaded?.id) {
            const id = Number(uploaded.id);
            if (id) {
              setSelectedStatementIds((prev) => {
                const arr = Array.isArray(prev) ? prev : [];
                return arr.includes(id) ? arr : [...arr, id];
              });
            }
          }
          loadStatements();
        }}
        onRunMatching={(next) => {
          if (next?.id) {
            const id = Number(next.id);
            if (id) {
              setSelectedStatementsById((prev) => ({ ...(prev || {}), [id]: next }));
              setSelectedStatementIds((prev) => {
                const arr = Array.isArray(prev) ? prev : [];
                return arr.includes(id) ? arr : [...arr, id];
              });
            }
          }
          load();
          loadStatements();
        }}
      />
    </div>
  );
}
